import { memo } from 'react';
import { NodeProps, Handle, Position } from 'reactflow';
import { palette } from '../../lib/palette';
import { BaseNode } from './BaseNode';

// Cores das saídas do modo switch — sequência fixa do design system
const CASE_COLORS = [
  palette.blue.base,
  palette.purple.base,
  palette.green.dark1,
  palette.yellow.dark2,
  palette.red.base,
  palette.blue.dark1,
  palette.purple.dark2,
];
const caseColor = (index: number) => CASE_COLORS[index % CASE_COLORS.length];
const DEFAULT_COLOR = palette.gray.base;

const OUTPUT_MIN_WIDTH = 72;

export const ConditionNode = memo((props: NodeProps) => {
  const config = props.data.config;
  const mode = config?.mode || 'simple';
  const cases: any[] = config?.cases || [];

  const getDescription = () => {
    if (mode === 'switch') return `${cases.length} condição(ões) + padrão`;

    const type = config?.conditionType;
    if (!type) return 'Defina a regra de desvio';

    const labels: Record<string, string> = {
      contains: 'Contém texto',
      equals: 'Igual a',
      regex: 'Expressão regular',
      variable: 'Variável',
    };
    return labels[type] || type;
  };

  const outputs = mode === 'switch'
    ? [
        ...cases.map((c, i) => ({ id: `case-${i}`, label: c.label || c.value || `Caso ${i + 1}`, color: caseColor(i) })),
        { id: 'default', label: 'Padrão', color: DEFAULT_COLOR },
      ]
    : [
        { id: 'true', label: 'Sim', color: palette.green.dark1 },
        { id: 'false', label: 'Não', color: palette.red.base },
      ];

  // Saídas na base do bloco, cada uma sob o seu rótulo (o fluxo corre de cima para baixo)
  const width = Math.max(220, outputs.length * OUTPUT_MIN_WIDTH + 28);
  const leftFor = (index: number) => `${((index + 0.5) * 100) / outputs.length}%`;

  return (
    <BaseNode
      {...props}
      width={width}
      label={mode === 'switch' ? 'Condição múltipla' : 'Condição'}
      description={getDescription()}
      onClick={props.data.onClick}
      onDelete={props.data.onDelete}
      sourceHandles={
        <>
          <div style={{ display: 'flex', borderTop: `1px solid ${palette.gray.light2}` }}>
            {outputs.map((o) => (
              <div
                key={o.id}
                title={o.label}
                style={{
                  flex: 1,
                  minWidth: 0,
                  padding: '6px 4px 8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 5,
                  fontSize: 11,
                  fontWeight: 500,
                  color: palette.gray.dark2,
                }}
              >
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: o.color, flexShrink: 0 }} />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.label}</span>
              </div>
            ))}
          </div>
          {outputs.map((o, i) => (
            <Handle
              key={o.id}
              type="source"
              position={Position.Bottom}
              id={o.id}
              style={{ width: 10, height: 10, background: o.color, border: '2px solid var(--surface)', left: leftFor(i) }}
              title={o.label}
            />
          ))}
        </>
      }
    />
  );
});

ConditionNode.displayName = 'ConditionNode';
